import { Button } from '@amore/ui/components/button'
import { Field } from '@amore/ui/components/field'
import { Input } from '@amore/ui/components/input'
import { Textarea } from '@amore/ui/components/textarea'
import { supplierCoverImage } from '@amore/ui/supplier-covers'
import { Link } from '@tanstack/react-router'
import { Globe, ImagePlus, Mail, MapPin, PenLine, Phone } from 'lucide-react'
import { type ReactNode, useState } from 'react'
import type { SupplierProfile } from '@/mock/types'
import { useToast } from '@/shell/toaster'
import { categoryLabel } from './model'

/** lucide has no brand icons; these are simple outline marks in the same 24px stroke style. */
function BrandIcon({ name }: { name: 'instagram' | 'facebook' | 'tiktok' | 'youtube' }) {
  const paths = {
    instagram: (
      <>
        <rect x="3" y="3" width="18" height="18" rx="5" />
        <circle cx="12" cy="12" r="4" />
        <circle cx="17.5" cy="6.5" r="0.6" fill="currentColor" />
      </>
    ),
    facebook: (
      <path d="M15 3h-2.5A3.5 3.5 0 0 0 9 6.5V10H6.5v3.5H9V21h3.5v-7.5H15l.5-3.5h-3V7a1 1 0 0 1 1-1H15Z" />
    ),
    tiktok: <path d="M14 3v11.5a3.5 3.5 0 1 1-3.5-3.5M14 3c.4 2.6 2.1 4.3 5 4.6" />,
    youtube: (
      <>
        <rect x="2.5" y="5.5" width="19" height="13" rx="4" />
        <path d="m10.5 9.5 4 2.5-4 2.5Z" fill="currentColor" />
      </>
    ),
  }
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="size-4"
    >
      {paths[name]}
    </svg>
  )
}

const links: {
  key: 'website' | 'instagram' | 'facebook' | 'tiktok' | 'youtube'
  label: string
  icon: ReactNode
  href: (value: string) => string
}[] = [
  {
    key: 'website',
    label: 'Web',
    icon: <Globe aria-hidden="true" className="size-4" />,
    href: (v) => `https://${v.replace(/^https?:\/\//, '')}`,
  },
  {
    key: 'instagram',
    label: 'Instagram',
    icon: <BrandIcon name="instagram" />,
    href: (v) => `https://instagram.com/${v}`,
  },
  {
    key: 'facebook',
    label: 'Facebook',
    icon: <BrandIcon name="facebook" />,
    href: (v) => `https://facebook.com/${v}`,
  },
  {
    key: 'tiktok',
    label: 'TikTok',
    icon: <BrandIcon name="tiktok" />,
    href: (v) => `https://tiktok.com/@${v}`,
  },
  {
    key: 'youtube',
    label: 'YouTube',
    icon: <BrandIcon name="youtube" />,
    href: (v) => `https://youtube.com/@${v}`,
  },
]

const editFields: { key: keyof SupplierProfile; label: string; placeholder: string }[] = [
  { key: 'website', label: 'Web', placeholder: 'www.example.cz' },
  { key: 'email', label: 'E-mail', placeholder: 'info@example.cz' },
  { key: 'phone', label: 'Telefon', placeholder: '+420 …' },
  { key: 'address', label: 'Adresa', placeholder: 'Ulice, město' },
  { key: 'instagram', label: 'Instagram', placeholder: 'uživatelské jméno' },
  { key: 'facebook', label: 'Facebook', placeholder: 'stránka' },
  { key: 'tiktok', label: 'TikTok', placeholder: 'uživatelské jméno' },
  { key: 'youtube', label: 'YouTube', placeholder: 'kanál' },
]

/** Mocked imagery: the category's cover first, then the other stock covers as a gallery. */
function catalogImages(category: string) {
  const cover = supplierCoverImage(category)
  const others = ['venue', 'photographer', 'florist', 'catering']
    .map(supplierCoverImage)
    .filter((image) => image !== cover)
  return { cover, gallery: [cover, ...new Set(others)] }
}

/**
 * The supplier's public profile, the way it looks in the catalog: cover photo,
 * name, category and area, links to the web and social networks, description,
 * gallery and contact details. A catalog supplier's profile belongs to the
 * catalog and is read-only; the couple's own supplier can fill it in here
 * ("Upravit profil"), and the sheet's "Uložit" saves it.
 */
export function ProfilePane({
  name,
  category,
  catalog,
  profile,
  editable,
  onChange,
  onNameChange,
  inCatalog = false,
}: {
  name: string
  category: string
  catalog?: { id: string; region: string }
  profile: SupplierProfile
  /** The couple's own supplier and a role that may edit. */
  editable: boolean
  onChange: (profile: SupplierProfile) => void
  onNameChange: (name: string) => void
  /** Shown in the catalog itself: no link back to it. */
  inCatalog?: boolean
}) {
  const { showToast } = useToast()
  const [editing, setEditing] = useState(false)
  const images = catalog ? catalogImages(category) : undefined
  const shownLinks = links.filter((link) => profile[link.key])
  const contacts = [
    profile.email && {
      icon: Mail,
      value: profile.email,
      href: `mailto:${profile.email}`,
    },
    profile.phone && {
      icon: Phone,
      value: profile.phone,
      href: `tel:${profile.phone.replaceAll(' ', '')}`,
    },
    profile.address && { icon: MapPin, value: profile.address },
  ].filter(Boolean) as { icon: typeof Mail; value: string; href?: string }[]
  const addPhotos = () => showToast({ title: 'Nahrávání fotek připravujeme' })

  return (
    <div>
      {images ? (
        <img src={images.cover} alt="" className="h-72 w-full object-cover" />
      ) : (
        <div className="grid h-56 place-items-center bg-muted/60">
          <div className="space-y-3 text-center">
            <ImagePlus aria-hidden="true" className="mx-auto size-7 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">Zatím bez fotek</p>
            {editable ? (
              <Button appearance="outline" tone="neutral" size="sm" onClick={addPhotos}>
                Přidat fotky
              </Button>
            ) : null}
          </div>
        </div>
      )}

      <div className="space-y-9 px-6 py-8 sm:px-10">
        <header className="space-y-3">
          <div className="flex items-start justify-between gap-4">
            <h2 className="font-heading text-4xl leading-tight tracking-tight">{name}</h2>
            {editable ? (
              <Button
                appearance="outline"
                tone="neutral"
                size="sm"
                onClick={() => setEditing((value) => !value)}
              >
                {editing ? (
                  'Hotovo'
                ) : (
                  <>
                    <PenLine aria-hidden="true" className="size-3.5" /> Upravit profil
                  </>
                )}
              </Button>
            ) : null}
          </div>
          <p className="text-sm text-muted-foreground">
            {[categoryLabel(category), catalog?.region].filter(Boolean).join(' · ')}
          </p>
          {!editing && shownLinks.length > 0 ? (
            <div className="flex flex-wrap gap-2 pt-1">
              {shownLinks.map((link) => (
                <a
                  key={link.key}
                  href={link.href(profile[link.key] as string)}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex min-h-9 items-center gap-2 rounded-full border border-border bg-card px-3.5 text-xs font-semibold transition-colors hover:bg-muted"
                >
                  {link.icon}
                  {link.label}
                </a>
              ))}
            </div>
          ) : null}
        </header>

        {editing ? (
          <fieldset className="grid gap-4 sm:grid-cols-2">
            <legend className="sr-only">Veřejný profil</legend>
            <Field label="Název" className="sm:col-span-2">
              <Input value={name} onChange={(event) => onNameChange(event.target.value)} />
            </Field>
            <Field label="Popis" className="sm:col-span-2">
              <Textarea
                value={profile.description ?? ''}
                placeholder="Čím se dodavatel zabývá, styl, pro koho je…"
                onChange={(event) => onChange({ ...profile, description: event.target.value })}
                className="min-h-24"
              />
            </Field>
            {editFields.map((field) => (
              <Field
                key={field.key}
                label={field.label}
                className={field.key === 'address' ? 'sm:col-span-2' : undefined}
              >
                <Input
                  value={profile[field.key] ?? ''}
                  placeholder={field.placeholder}
                  onChange={(event) => onChange({ ...profile, [field.key]: event.target.value })}
                />
              </Field>
            ))}
          </fieldset>
        ) : (
          <>
            <section className="space-y-3">
              <h3 className="text-sm font-semibold">O dodavateli</h3>
              <p className="max-w-prose text-[15px] leading-relaxed">
                {profile.description || (
                  <span className="text-muted-foreground">Zatím bez popisu.</span>
                )}
              </p>
            </section>

            {images ? (
              <section className="space-y-3">
                <h3 className="text-sm font-semibold">Galerie</h3>
                <div className="grid grid-cols-3 gap-3">
                  {images.gallery.map((image, index) => (
                    <img
                      key={image}
                      src={image}
                      alt=""
                      className={
                        index === 0
                          ? 'col-span-2 row-span-2 h-full w-full rounded-2xl object-cover'
                          : 'aspect-[4/3] w-full rounded-2xl object-cover'
                      }
                    />
                  ))}
                </div>
              </section>
            ) : null}

            {contacts.length > 0 ? (
              <section className="space-y-3">
                <h3 className="text-sm font-semibold">Kontakt</h3>
                <ul className="space-y-2.5 text-sm">
                  {contacts.map((item) => (
                    <li key={item.value} className="flex items-center gap-3">
                      <item.icon aria-hidden="true" className="size-4 text-muted-foreground" />
                      {item.href ? (
                        <a href={item.href} className="hover:underline">
                          {item.value}
                        </a>
                      ) : (
                        item.value
                      )}
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}
          </>
        )}

        {catalog && !inCatalog ? (
          <Link
            to={`/suppliers/catalog/${catalog.id}`}
            className="inline-block text-sm font-semibold text-action hover:underline"
          >
            Otevřít v katalogu ↗
          </Link>
        ) : null}
      </div>
    </div>
  )
}
